import { useState } from "react";
import { toast } from "sonner";
import { Modal } from "@/components/Modals";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { inr } from "@/lib/format";

/**
 * Send goods back to the supplier against a purchase (owner, 2026-11-01). Each line
 * can go back at most what arrived less what already went back; cost and tax come
 * from the purchase line, so the server works out the money.
 */
const qty = value => Number(value ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });

export default function ReturnDialog({ order, onSave, onClose }) {
  const returnable = order.items
    .map(line => ({ ...line, left: Math.max(0, Number(line.quantity_received) - Number(line.quantity_returned || 0)) }))
    .filter(line => line.left > 0);
  const [amounts, setAmounts] = useState({});
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState(null);

  const submit = async () => {
    const items = [];
    for (const line of returnable) {
      const raw = amounts[line.id];
      if (raw === undefined || raw === "") continue;
      const value = Number(raw);
      if (!Number.isFinite(value) || value <= 0) return toast.error(`${line.item_name}: enter a quantity above zero`);
      if (value > line.left) return toast.error(`${line.item_name}: at most ${qty(line.left)} ${line.unit} can go back`);
      items.push({ purchase_order_item_id: line.id, quantity: value });
    }
    if (!items.length) return toast.error("Enter what is going back");
    setSaving(true);
    const result = await onSave({ supplier_id: order.supplier_id, purchase_order_id: order.id,
                                  reason: reason.trim() || null, items });
    setSaving(false);
    if (!result.ok) {
      setFailure(result.error);
      return toast.error(result.error);
    }
    toast.success(`${result.data.return_number} saved · supplier owed ${inr(result.data.total)} less`);
    onClose();
  };

  return (
    <Modal title={`Return goods · ${order.po_number}`} onClose={onClose} testId="return-modal"
           footer={<><Button variant="outline" onClick={onClose}>Cancel</Button>
             <Button variant="action" disabled={saving || !returnable.length} onClick={submit} data-testid="save-return-button">
               {saving ? "Saving…" : "Save return"}
             </Button></>}>
      <div className="space-y-base">
        {!returnable.length && <p className="text-meta text-muted-foreground">Everything that arrived has already gone back.</p>}
        {returnable.map(line => (
          <div key={line.id} className="flex items-center gap-snug" data-testid={`return-line-${line.id}`}>
            <span className="flex-1 min-w-0">
              <span className="block text-body font-semibold truncate">{line.item_name}</span>
              <span className="text-micro text-muted-foreground">
                arrived {qty(line.quantity_received)} · back already {qty(line.quantity_returned || 0)} · {inr(line.unit_cost)} each
              </span>
            </span>
            <Input type="number" min="0" step="0.001" className="w-28 text-right tabular-nums" placeholder="0"
                   value={amounts[line.id] ?? ""} aria-label={`Return ${line.item_name}`}
                   onChange={e => setAmounts(a => ({ ...a, [line.id]: e.target.value }))}
                   data-testid={`return-line-${line.id}-qty`} />
            <span className="w-10 text-meta text-muted-foreground">{line.unit}</span>
          </div>
        ))}
        <label className="flex flex-col gap-tight text-meta font-medium">Why
          <Input value={reason} maxLength={500} placeholder="e.g. 2 kg tomatoes rotten" onChange={e => setReason(e.target.value)}
                 data-testid="return-reason" />
        </label>
        <p className="text-micro text-muted-foreground">Stock goes out now; the return can carry the supplier's credit note as a file after saving.</p>
        {failure && <p className="text-body font-semibold text-destructive" data-testid="return-error">{failure}</p>}
      </div>
    </Modal>
  );
}
